/**
 * `?flag=off` — the feature flag, which is the first of the nine eligibility checks.
 *
 * ```
 * ?flag=off   the flow is not enabled for this context → FEATURE_DISABLED
 * ?flag=on    enabled, and clears a held switch
 * ```
 *
 * `FEATURE_DISABLED` is one of only three triggers in the fourteen that carries no
 * `@unspecified` marker in the schema — the block itself is not in doubt. What is in
 * doubt is everything about the flag: Q-27 asks what it is targeted on, who can turn
 * it on, and whether a customer half-way through the flow when it goes off is stopped
 * or allowed to finish. This switch answers none of that. It makes the one state the
 * flag produces visible, which is all a POC can honestly do with it.
 *
 * ### Why there is no `featureFlagGuard`
 *
 * `session.guard.ts` says it: the flag is step 1 of the eligibility chain in
 * `eligibility.service.ts`, and it arrives on the wire as a `FEATURE_DISABLED` block
 * on `Session` like the other thirteen. A guard that asked separately would be a
 * second place where the flow decides whether it is available, and the two would
 * eventually disagree. So this switch flips the flag in the mock BFF and everything
 * downstream — the guard, the shell's block rendering, the reference line — behaves
 * exactly as it does for any other block.
 *
 * ### Persistence
 *
 * Held in `sessionStorage` like the other switches, because the flag is read on the
 * bootstrap of every screen and a query string does not survive the first in-app
 * navigation. `FeatureFlags` itself is a field on a service — not persisted, so a
 * reload with the switch cleared has the flow enabled again, and
 * `DemoControls.reset()` does not need to know about it.
 *
 * POC scaffolding. In production the flag is set by whatever Orange decides Q-27 is.
 */
import { inject, provideAppInitializer } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import type { DemoControls } from '../../mock-bff/mock-backend';
import { MockBackend } from '../../mock-bff/mock-backend';

/** On is the default, so `off` is the only value that does anything. */
export type FlagSwitch = 'on' | 'off';

const STORAGE_KEY = 'pre2post.poc.flag.v1';

/** Applies a held or URL-supplied switch at startup. */
export function provideFeatureFlagSwitch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    if (read() !== 'off') return;
    // Resolved through the injector rather than constructed: the flag lives on the
    // eligibility service inside `MockBackend`, and a second instance would carry a
    // second flag that nothing reads.
    applyFlagSwitch(inject(MockBackend).controls, 'off');
  });
}

/** Holds the choice for the rest of the tab's life. Persists only; see the header. */
export function setFlagSwitch(value: FlagSwitch): void {
  try {
    if (value === 'on') sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private browsing. The switch applies to this page load and no further.
  }
}

export function clearFlagSwitch(): void {
  setFlagSwitch('on');
}

/**
 * The effect, separated from the persistence so the panel can apply it without a
 * reload. Takes `DemoControls` so it is callable outside an injection context.
 *
 * On its own this is not enough to change what the screen shows: the eligibility chain
 * is resolved once at bootstrap and cached on `SessionStore`, so the panel follows this
 * with `SessionStore.reload()` and a same-URL re-navigation — the same pair the block's
 * own `retry` action runs.
 */
export function applyFlagSwitch(controls: DemoControls, value: FlagSwitch): void {
  controls.setFeatureEnabled(value === 'on');
}

/** What is held right now. The panel's display value; `on` when nothing is. */
export function currentFlagSwitch(): FlagSwitch {
  try {
    return parseFlagSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '') ?? 'on';
  } catch {
    return 'on';
  }
}

/** The URL wins over the held value, and replaces it. */
function read(): FlagSwitch | null {
  try {
    const fromUrl = new URLSearchParams(location.search).get('flag');
    if (fromUrl !== null) {
      const parsed = parseFlagSwitch(fromUrl);
      if (parsed !== null) {
        setFlagSwitch(parsed);
        return parsed;
      }
    }
    return parseFlagSwitch(sessionStorage.getItem(STORAGE_KEY) ?? '');
  } catch {
    return null;
  }
}

export function parseFlagSwitch(value: string): FlagSwitch | null {
  const trimmed = value.trim().toLowerCase();
  // `false`/`true` accepted because a flag is the one switch people type as a boolean.
  if (trimmed === 'off' || trimmed === 'false' || trimmed === '0') return 'off';
  if (trimmed === 'on' || trimmed === 'true' || trimmed === '1') return 'on';
  return null;
}
